import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Buildings, ArrowsLeftRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const DATE_PRESETS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "All time", days: null },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Owner view across every brand.
 *
 * This is the only screen that reads more than one business's data. Each row is
 * fetched server-side inside that brand's own database — nothing is joined, so
 * a brand with a problem shows an error on its own row instead of blanking the
 * page.
 */
export default function Businesses() {
  const { user, switchBusiness } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState(30);

  const load = async (days) => {
    setLoading(true);
    try {
      const params = days ? { date_from: isoDaysAgo(days) } : {};
      const { data } = await api.get("/businesses/rollup", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_super_admin) load(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, user?.is_super_admin]);

  // Guard in the UI as well as the API — a staff admin landing here by URL
  // should be bounced, not shown an empty shell.
  if (user && !user.is_super_admin) return <Navigate to="/" replace />;

  const rows = data?.businesses || [];
  const totals = data?.totals || {};
  const maxLeads = Math.max(1, ...rows.map((r) => r.leads || 0));

  const go = async (slug) => {
    const res = await switchBusiness(slug);
    if (res.ok) toast.success(`Switched to ${slug}`);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Platform"
        title="All businesses"
        description="Combined view across every brand on the platform. Click a row to switch into that business."
      />

      <div className="px-8 pb-10 space-y-6">
        <div className="flex items-center gap-2">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setPreset(p.days)}
              data-testid={`rollup-preset-${p.label.replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border transition-colors ${
                preset === p.days
                  ? "border-primary bg-primary/5 text-primary font-semibold"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {[
            ["Businesses", rows.length],
            ["Total leads", (totals.leads ?? 0).toLocaleString()],
            ["Converted", (totals.converted ?? 0).toLocaleString()],
            ["Conversion", `${totals.conversion_rate ?? 0}%`],
          ].map(([label, value]) => (
            <div key={label} className="bg-white p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
              <div className="font-display text-3xl font-bold mt-2 tracking-tight">{value}</div>
            </div>
          ))}
        </div>

        {/* Per-business */}
        <div className="border border-border">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-border bg-secondary/40 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            <div className="col-span-4">Business</div>
            <div className="col-span-3">Leads</div>
            <div className="col-span-2 text-right">Converted</div>
            <div className="col-span-2 text-right">Conv. rate</div>
            <div className="col-span-1" />
          </div>

          {loading && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          )}

          {!loading && rows.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              No businesses configured.
            </div>
          )}

          {!loading &&
            rows.map((b) => (
              <div
                key={b.slug}
                className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-secondary/30"
                data-testid={`rollup-row-${b.slug}`}
              >
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <div
                    className="w-7 h-7 shrink-0 flex items-center justify-center text-white"
                    style={{ backgroundColor: b.branding?.primary_color || "#002FA7" }}
                  >
                    <Buildings size={15} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{b.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {b.slug}
                      {b.slug === user?.business && " · current"}
                    </div>
                  </div>
                </div>

                {b.error ? (
                  <div className="col-span-7 flex items-center gap-2 text-sm text-destructive">
                    <Warning size={16} />
                    <span className="truncate" title={b.error}>
                      Could not read this business: {b.error}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="col-span-3">
                      <div className="text-sm font-semibold">{(b.leads || 0).toLocaleString()}</div>
                      <div className="h-1.5 bg-secondary mt-1.5">
                        <div
                          className="h-full"
                          style={{
                            width: `${((b.leads || 0) / maxLeads) * 100}%`,
                            backgroundColor: b.branding?.primary_color || "#002FA7",
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-span-2 text-right text-sm">
                      {(b.converted || 0).toLocaleString()}
                    </div>
                    <div className="col-span-2 text-right text-sm font-semibold">
                      {b.conversion_rate ?? 0}%
                    </div>
                  </>
                )}

                <div className="col-span-1 flex justify-end">
                  {b.slug !== user?.business && (
                    <button
                      onClick={() => go(b.slug)}
                      title={`Switch to ${b.name}`}
                      data-testid={`switch-to-${b.slug}`}
                      className="p-2 hover:bg-secondary transition-colors"
                    >
                      <ArrowsLeftRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
